import Alert from '@mui/material/Alert';
import { optionalFieldLabel } from '@openx/components-form/optionalFieldLabel';
import { type DealFormData, dealFormField } from '@openx/types/deal';
import type { HierarchyItemWithLevels } from '@openx/types/hierarchyItem';
import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../utils/context/DealContext';
import { HierarchyAutoComplete } from '../HierarchyAutoComplete/HierarchyAutoComplete';
import { NoOptionsInfo } from './components/NoOptionsInfo';

export const AgencyAutoComplete = () => {
  const { t } = useTranslation();
  const { useAgenciesOptionsFetch } = useDealContext();
  const agencies = useAgenciesOptionsFetch();
  const { setValue } = useFormContext<DealFormData>();

  const observableAgencySalesforceID =
    useWatch<Pick<DealFormData, 'agency_salesforce_id'>>({
      name: dealFormField.AGENCY_SALESFORCE_ID,
    }) || '';

  const onAgencySelect = useCallback(
    (e: HierarchyItemWithLevels | null): void => {
      setValue(dealFormField.AGENCY_SALESFORCE_ID, e?.id ?? null, { shouldDirty: true });
    },
    [setValue]
  );

  const noOptionsText = useCallback(() => <NoOptionsInfo />, []);

  if (agencies.loading) {
    return (
      <HierarchyAutoComplete
        data={[]}
        dataMap={{}}
        isLoading={agencies.loading}
        onOptionSelect={onAgencySelect}
        value={''}
        placeholderText={t('Search an Agency')}
        noOptionsText={noOptionsText}
        inputLabel={optionalFieldLabel(t('Agency'))}
        testId="agency-autocomplete"
      />
    );
  }

  return (
    <>
      {observableAgencySalesforceID && agencies.mapById[observableAgencySalesforceID] === undefined && (
        <Alert severity="warning" sx={theme => ({ marginTop: theme.spacing(1) })}>
          {t('The agency has been removed from Salesforce')}
        </Alert>
      )}

      <HierarchyAutoComplete
        data={agencies.list}
        dataMap={agencies.mapById}
        isLoading={agencies.loading}
        onOptionSelect={onAgencySelect}
        value={observableAgencySalesforceID}
        placeholderText={t('Search an Agency')}
        noOptionsText={noOptionsText}
        inputLabel={optionalFieldLabel(t('Agency'))}
        testId="agency-autocomplete"
      />
    </>
  );
};
